import React, { useEffect, useMemo, useState } from "react";
import { Gift, TrendingDown, X, Loader2, Lock, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { apiFetch } from "../../../../../context/AuthContext";
import ReusableDropdown from "../../../../../components/ReusableDropdown";

// One modal for both bulk actions — "add allowance/bonus to everyone" and
// "add deduction to everyone". It posts to the SAME per-payroll endpoints
// the single-row Allowances/Deductions modals use, once per selected
// employee, so the backend's rules (paid-lock, Gross→Net conversion,
// recalculation, employee notification) apply exactly as they do one by one.
//
// Type lists deliberately leave out types the backend manages by itself —
// they would be silently overwritten or wiped:
//   • transportation  → writes the employee's permanent transportation field
//                       (REPLACES it, doesn't add) instead of a payroll line
//   • overtime        → wiped and rebuilt from attendance on every payroll run
//   • advancement / medical_insurance / undertime / new_comer → same cleanup
//   • absence / late_arrival / sick leave → day/hour-based, need per-employee input

const KINDS = {
  allowance: {
    endpoint: "allowances",
    taxField: "isTaxable",
    defaultGross: true, // same default as the single-row AddAllowanceForm
    accent: "#10b981",
    accentDark: "#059669",
    icon: Gift,
    title: ["إضافة مكافأة / بدل للمحددين", "Add bonus / allowance to selection"],
    defaultDescription: ["مكافأة جماعية", "Bulk allowance"],
    types: [
      ["bonus", "مكافأة", "Bonus"],
      ["incentive", "حافز", "Incentive"],
      ["performance", "مكافأة أداء", "Performance"],
      ["achievement", "إنجاز", "Achievement"],
      ["kpi", "KPI", "KPI"],
      ["commission", "عمولة", "Commission"],
      ["eid_bonus", "منحة عيد", "Eid Bonus"],
      ["ramadan_bonus", "منحة رمضان", "Ramadan Bonus"],
      ["seasonal_bonus", "منحة موسمية", "Seasonal Bonus"],
      ["housing", "بدل سكن", "Housing"],
      ["meal", "بدل وجبات", "Meal"],
      ["representation", "بدل تمثيل", "Representation"],
      ["expenses", "مصروفات", "Expenses"],
      ["adjustment", "تسوية", "Adjustment"],
      ["other", "أخرى", "Other"],
    ],
    grossHelp: [
      "المبلغ ده «إجمالي» قبل الضريبة والتأمينات. السيستم هيحوّله لأثره الحقيقي على الصافي — يعني الموظف هيستلم أقل من الرقم اللي كتبته، والرقم الأصغر ده هو اللي هيتسجل.",
      "This is a GROSS amount (before tax & insurance). It's converted to its real net impact, so the employee receives less than you type — that smaller figure is what gets stored.",
    ],
    netHelp: [
      "المبلغ ده «صافي»: الموظف هيستلمه في إيده بالظبط، والشركة هي اللي بتتحمل أي ضريبة عليه.",
      "This is a NET amount: the employee receives exactly this; the company absorbs any tax on it.",
    ],
    submit: ["إضافة لـ", "Add to"],
  },
  deduction: {
    endpoint: "deductions",
    taxField: "isPreTax",
    defaultGross: false, // same default as the single-row AddDeductionForm
    accent: "#ef4444",
    accentDark: "#dc2626",
    icon: TrendingDown,
    title: ["إضافة خصم على المحددين", "Add deduction to selection"],
    defaultDescription: ["خصم جماعي", "Bulk deduction"],
    types: [
      ["penalty", "جزاء", "Penalty"],
      ["salary_advance", "سلفة", "Salary Advance"],
      ["emergency_fund", "صندوق طوارئ", "Emergency Fund"],
      ["aman_card", "كارت أمان", "Aman Card"],
      ["premium_card", "بريميوم كارد", "Premium Card"],
      ["adjustment", "تسوية", "Adjustment"],
      ["other", "أخرى", "Other"],
    ],
    grossHelp: [
      "المبلغ ده «إجمالي»: بيتخصم من المرتب قبل الضريبة، فالصافي هيقل بأقل من الرقم اللي كتبته (لأن جزء منه كان أصلاً هيروح ضرايب وتأمينات).",
      "GROSS: taken off before tax, so take-home pay drops by LESS than you type (part of it would have gone to tax anyway).",
    ],
    netHelp: [
      "المبلغ ده «صافي»: هيتخصم بالظبط من صافي المرتب اللي الموظف هيستلمه.",
      "NET: exactly this amount is removed from the employee's take-home pay.",
    ],
    submit: ["خصم من", "Deduct from"],
  },
};

const MAX_AMOUNT = 10_000_000;

const BulkAdjustmentModal = ({
  kind,
  isOpen,
  onClose,
  selectedEmployees = [],
  API_BASE_URL,
  theme,
  isRTL,
  showAlert,
  onSuccess,
}) => {
  const cfg = KINDS[kind];
  const L = (pair) => (isRTL ? pair[0] : pair[1]);
  const isDark = theme === "dark";

  const [type, setType] = useState(cfg.types[0][0]);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isGross, setIsGross] = useState(cfg.defaultGross);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [failures, setFailures] = useState(null); // [{ id, infinityCode, reason }]

  // Paid payrolls are locked server-side ("Cannot add … already paid"), so
  // they're excluded up front instead of being counted as silent failures.
  const eligible = useMemo(() => selectedEmployees.filter((e) => e.status !== "paid"), [selectedEmployees]);
  const paidCount = selectedEmployees.length - eligible.length;

  const numAmount = Math.round((parseFloat(amount) || 0) * 100) / 100;
  const amountValid = Number.isFinite(numAmount) && numAmount > 0 && numAmount <= MAX_AMOUNT;
  const canSubmit = amountValid && eligible.length > 0 && !running;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !running) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, running, onClose]);

  if (!isOpen || !cfg) return null;

  const fmt = (n) => Number(n).toLocaleString(isRTL ? "ar-EG" : "en-US", { maximumFractionDigits: 2 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (!amountValid) {
        showAlert(L(["تنبيه", "Warning"]), L(["اكتب مبلغ أكبر من صفر", "Enter an amount greater than zero"]), "info");
      }
      return;
    }

    setRunning(true);
    setDone(0);
    setFailures(null);
    const failed = [];
    let ok = 0;

    for (let i = 0; i < eligible.length; i++) {
      const emp = eligible[i];
      try {
        const res = await apiFetch(`${API_BASE_URL}/payrollX/${emp.id}/${cfg.endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type,
            amount: numAmount,
            description: description.trim() || L(cfg.defaultDescription),
            [cfg.taxField]: isGross,
          }),
        });
        if (res.ok) {
          ok++;
        } else {
          const body = await res.json().catch(() => ({}));
          failed.push({ id: emp.id, infinityCode: emp.infinityCode, reason: body?.error || `HTTP ${res.status}` });
        }
      } catch (err) {
        failed.push({ id: emp.id, infinityCode: emp.infinityCode, reason: err.message });
      }
      setDone(i + 1);
    }

    setRunning(false);
    if (ok > 0) onSuccess?.({ keepSelection: failed.length > 0 });

    if (failed.length === 0) {
      showAlert(
        L(["تم", "Done"]),
        isRTL ? `تم التطبيق بنجاح على ${ok} موظف` : `Applied to ${ok} employee(s)`,
        "success",
      );
      onClose();
    } else {
      // Stay open and show exactly who failed and why.
      setFailures(failed);
    }
  };

  const Icon = cfg.icon;
  const progress = eligible.length ? Math.round((done / eligible.length) * 100) : 0;
  const text = isDark ? "#f1f5f9" : "#0f172a";
  const muted = isDark ? "#94a3b8" : "#64748b";
  const fieldBorder = isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1";
  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 14px",
    borderRadius: "10px",
    border: `1px solid ${fieldBorder}`,
    background: isDark ? "#1e293b" : "#f8fafc",
    color: text,
    fontSize: "14px",
    outline: "none",
  };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600, color: text, marginBottom: "8px" };

  const segBtn = (active) => ({
    flex: 1,
    padding: "9px 10px",
    borderRadius: "8px",
    border: "none",
    background: active ? cfg.accent : "transparent",
    color: active ? "#ffffff" : muted,
    fontSize: "13px",
    fontWeight: 700,
    cursor: running ? "not-allowed" : "pointer",
    transition: "all 0.15s ease",
  });

  return (
    <div
      className="payroll-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !running) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(10, 15, 29, 0.72)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={L(cfg.title)}
        style={{
          width: "100%",
          maxWidth: "520px",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          background: isDark ? "#0f172a" : "#ffffff",
          borderRadius: "18px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)",
          animation: "fadeInUp 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px",
            background: `linear-gradient(135deg, ${cfg.accent} 0%, ${cfg.accentDark} 100%)`,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon size={22} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>{L(cfg.title)}</h3>
              <p style={{ margin: "2px 0 0", fontSize: "13px", opacity: 0.92 }}>
                {isRTL
                  ? `هيتطبق على ${eligible.length} موظف`
                  : `Will apply to ${eligible.length} employee(s)`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            aria-label={L(["إغلاق", "Close"])}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              cursor: running ? "not-allowed" : "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "22px" }}>
          {paidCount > 0 && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
                padding: "10px 12px",
                marginBottom: "18px",
                borderRadius: "10px",
                background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
                border: `1px solid ${isDark ? "rgba(245,158,11,0.35)" : "#fde68a"}`,
                color: isDark ? "#fcd34d" : "#92400e",
                fontSize: "12.5px",
                lineHeight: 1.6,
              }}
            >
              <Lock size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
              <span>
                {isRTL
                  ? `${paidCount} موظف مرتبهم «مدفوع» ومقفول، هيتم تخطيهم. لو محتاج تعدّل عليهم اعمل Unpay الأول.`
                  : `${paidCount} selected payroll(s) are PAID and locked — they'll be skipped. Unpay them first to change them.`}
              </span>
            </div>
          )}

          {/* Type */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>{L(["النوع", "Type"])}</label>
            <ReusableDropdown
              items={cfg.types.map(([id, ar, en]) => ({ id, label: isRTL ? `${ar} (${en})` : en }))}
              value={type}
              onChange={setType}
              disabled={running}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.label}
              theme={theme}
              language={isRTL ? "ar" : "en"}
            />
          </div>

          {/* Amount */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>{L(["المبلغ لكل موظف (جنيه)", "Amount per employee (EGP)"])}</label>
            <input
              type="number"
              inputMode="decimal"
              min="0.01"
              max={MAX_AMOUNT}
              step="0.01"
              required
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={running}
              placeholder="0.00"
              style={{ ...fieldStyle, fontSize: "16px", fontWeight: 700, direction: "ltr", textAlign: isRTL ? "right" : "left" }}
            />
          </div>

          {/* Gross / Net */}
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>{L(["طريقة الحساب", "Calculation basis"])}</label>
            <div
              style={{
                display: "flex",
                gap: "4px",
                padding: "4px",
                borderRadius: "10px",
                background: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
              }}
            >
              <button type="button" disabled={running} onClick={() => setIsGross(false)} style={segBtn(!isGross)}>
                {L(["صافي (Net)", "Net"])}
              </button>
              <button type="button" disabled={running} onClick={() => setIsGross(true)} style={segBtn(isGross)}>
                {L(["إجمالي (Gross)", "Gross"])}
              </button>
            </div>
            <p
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "flex-start",
                margin: "8px 2px 0",
                fontSize: "12.5px",
                lineHeight: 1.65,
                color: muted,
              }}
            >
              <Info size={14} style={{ flexShrink: 0, marginTop: "3px" }} />
              {L(isGross ? cfg.grossHelp : cfg.netHelp)}
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "18px" }}>
            <label style={labelStyle}>{L(["الوصف / السبب (اختياري)", "Description / reason (optional)"])}</label>
            <input
              type="text"
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={running}
              placeholder={L(cfg.defaultDescription)}
              style={fieldStyle}
            />
          </div>

          {/* Summary */}
          {amountValid && eligible.length > 0 && !failures && (
            <div
              style={{
                padding: "12px 14px",
                marginBottom: "18px",
                borderRadius: "10px",
                border: `1px dashed ${cfg.accent}80`,
                background: `${cfg.accent}12`,
                color: text,
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              {isRTL ? (
                <>
                  <b>{fmt(numAmount)} جنيه</b> ({isGross ? "إجمالي" : "صافي"}) × <b>{eligible.length}</b> موظف ={" "}
                  <b>{fmt(numAmount * eligible.length)} جنيه</b>
                  {isGross && <span style={{ color: muted }}> — الأثر الفعلي على الصافي هيكون أقل</span>}
                </>
              ) : (
                <>
                  <b>{fmt(numAmount)} EGP</b> ({isGross ? "gross" : "net"}) × <b>{eligible.length}</b> ={" "}
                  <b>{fmt(numAmount * eligible.length)} EGP</b>
                  {isGross && <span style={{ color: muted }}> — actual net impact will be lower</span>}
                </>
              )}
            </div>
          )}

          {/* Progress */}
          {running && (
            <div style={{ marginBottom: "18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: muted, marginBottom: "6px" }}>
                <span>
                  {isRTL ? `جاري التطبيق… ${done} / ${eligible.length}` : `Applying… ${done} / ${eligible.length}`}
                </span>
                <span>{progress}%</span>
              </div>
              <div style={{ height: "6px", borderRadius: "3px", background: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: cfg.accent, transition: "width 0.2s ease" }} />
              </div>
            </div>
          )}

          {/* Partial failure report */}
          {failures && (
            <div
              style={{
                marginBottom: "18px",
                padding: "12px 14px",
                borderRadius: "10px",
                background: isDark ? "rgba(239,68,68,0.1)" : "#fef2f2",
                border: `1px solid ${isDark ? "rgba(239,68,68,0.35)" : "#fecaca"}`,
                fontSize: "12.5px",
                color: isDark ? "#fca5a5" : "#991b1b",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, marginBottom: "6px" }}>
                <AlertTriangle size={16} />
                {isRTL
                  ? `نجح ${eligible.length - failures.length} وفشل ${failures.length}`
                  : `${eligible.length - failures.length} succeeded, ${failures.length} failed`}
              </div>
              <ul style={{ margin: 0, paddingInlineStart: "18px", maxHeight: "120px", overflowY: "auto", lineHeight: 1.7 }}>
                {failures.map((f) => (
                  <li key={f.id}>
                    <b>{f.infinityCode || `#${f.id}`}</b>: {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={running}
              style={{
                padding: "10px 18px",
                borderRadius: "10px",
                border: `1px solid ${fieldBorder}`,
                background: "transparent",
                color: muted,
                fontSize: "14px",
                fontWeight: 600,
                cursor: running ? "not-allowed" : "pointer",
              }}
            >
              {failures ? L(["إغلاق", "Close"]) : L(["إلغاء", "Cancel"])}
            </button>
            {!failures && (
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "none",
                  background: `linear-gradient(135deg, ${cfg.accent} 0%, ${cfg.accentDark} 100%)`,
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  opacity: canSubmit || running ? 1 : 0.55,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: `0 4px 12px ${cfg.accent}4d`,
                }}
              >
                {running ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {isRTL
                  ? `${cfg.submit[0]} ${eligible.length} موظف`
                  : `${cfg.submit[1]} ${eligible.length} employee(s)`}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkAdjustmentModal;
